<script setup lang="ts">
import {
  Loader2 as Loader2Icon,
  Trash2 as Trash2Icon,
  X as XIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
  GripVertical as GripVerticalIcon,
} from '@lucide/vue'
import { computed, onBeforeUnmount, ref, watch, nextTick, type ComponentPublicInstance } from 'vue'

import { showConfirm } from '@/services/dialogService'
import { requirePermission } from '@/services/permissionService'
import { deleteRoutePointAction } from '@/services/routePointActions'
import { showInfo } from '@/services/toastService'
import { isScreenSizeBelow, ScreenSize } from '@/services/uiService'
import { useLibraryStore } from '@/stores/libraryStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Tour, RoutePoint } from '@/types/tour'
import { computeRoutePointLabels, getRoutePointColor } from '@/utils/routePointGrouping'

const props = defineProps<{
  tour?: Tour
  expanded?: boolean
  trackClientId?: string | null
  canDeleteRoutePoints?: boolean
  /** 'comfortable' enlarges rows/markers to ≥44px touch targets for the planner; default 'compact'. */
  size?: 'compact' | 'comfortable'
  /** Show a drag handle and allow drag-to-reorder within a track (planner only). */
  reorderable?: boolean
  /** Hide the per-track group headers (e.g. "NEW TRACK 11:49 PM") — used by the single-track planner. */
  hideTrackHeaders?: boolean
}>()

const isComfortable = computed(() => props.size === 'comfortable')

const workspaceStore = useWorkspaceStore()
const libraryStore = useLibraryStore()
const uiStore = useUiStore()

const isLibraryContext = computed(() => !workspaceStore.clientId)

const emit = defineEmits<{
  'zoom-route-point': [tourId: string, routePointIndex: number]
  'update:expanded': [value: boolean]
  'reorder-route-points': [
    tourId: string,
    trackClientId: string | null,
    orderedRoutePointClientIds: string[],
  ]
}>()

const routePointListRef = ref<HTMLElement | null>(null)
const isDeletingAllRoutePoints = ref(false)

const isExpanded = ref(
  props.expanded !== undefined ? props.expanded : !isScreenSizeBelow(ScreenSize.MD),
)

watch(
  () => props.expanded,
  (newValue) => {
    if (newValue !== undefined) {
      isExpanded.value = newValue
    }
  },
  { immediate: true },
)

type RpItem = { routePoint: RoutePoint; tourId: string; index: number; label: string }
type RpGroup = {
  tourId: string
  trackClientId: string | null
  trackName: string
  trackColor?: string
  items: RpItem[]
}

const buildGroupsForTour = (tour: Tour): RpGroup[] => {
  const labels = computeRoutePointLabels(tour.routePoints)
  const items: RpItem[] = tour.routePoints.map((rp, index) => ({
    routePoint: rp,
    tourId: tour.clientId,
    index,
    label: labels.get(rp.clientId) ?? String(index),
  }))
  const trackOrder: { clientId: string | null; name: string; color?: string }[] = (
    tour.tracks ?? []
  ).map((t) => ({ clientId: t.clientId ?? null, name: t.name || 'Unnamed Track', color: t.color }))
  const groups: RpGroup[] = []
  for (const track of trackOrder) {
    const groupItems = items.filter(
      (it) => (it.routePoint.trackClientId ?? null) === track.clientId,
    )
    if (groupItems.length === 0) continue
    groups.push({
      tourId: tour.clientId,
      trackClientId: track.clientId,
      trackName: track.name,
      trackColor: track.color,
      items: groupItems,
    })
  }
  // Unassigned RPs (no matching track)
  const assignedTrackIds = new Set(trackOrder.map((t) => t.clientId))
  const unassigned = items.filter(
    (it) => !assignedTrackIds.has(it.routePoint.trackClientId ?? null),
  )
  if (unassigned.length > 0) {
    groups.push({
      tourId: tour.clientId,
      trackClientId: null,
      trackName: tour.tracks && tour.tracks.length > 0 ? 'Unassigned' : 'Route Points',
      items: unassigned,
    })
  }
  return groups
}

const routePointGroups = computed<RpGroup[]>(() => {
  const selectedTrackClientId = props.trackClientId ?? undefined
  const filterGroups = (groups: RpGroup[]) =>
    selectedTrackClientId === undefined
      ? groups
      : groups.filter((group) => (group.trackClientId ?? null) === (selectedTrackClientId ?? null))

  if (props.tour) return filterGroups(buildGroupsForTour(props.tour))
  const groups: RpGroup[] = []
  workspaceStore.tours.forEach((tour: Tour) => {
    if (tour.active) groups.push(...buildGroupsForTour(tour))
  })
  return filterGroups(groups)
})

const routePoints = computed(() => routePointGroups.value.flatMap((g) => g.items))

const bulkDeletableRoutePoints = computed(() =>
  routePointGroups.value.flatMap((group) =>
    group.items.length <= 2 ? [] : group.items.slice(1, -1),
  ),
)

const canBulkDeleteRoutePoints = computed(() => bulkDeletableRoutePoints.value.length > 0)
const isBulkDeleteDisabled = computed(
  () => !canBulkDeleteRoutePoints.value || isDeletingAllRoutePoints.value,
)

const deleteRoutePoint = async (tourId: string, rpIndex: number) => {
  await deleteRoutePointAction(tourId, rpIndex)
}

const deleteAllRoutePoints = async () => {
  const deletableRoutePoints = bulkDeletableRoutePoints.value
  const count = deletableRoutePoints.length
  if (count === 0) {
    showInfo('A and B must stay in place. There are no intermediate route points to delete.')
    return
  }
  if (!(await requirePermission('delete_waypoint'))) return

  const confirmed = await showConfirm({
    title: 'Delete Intermediate Route Points',
    message: `Are you sure you want to delete ${count} route point${count > 1 ? 's' : ''}? Route points A and B will be kept.`,
    confirmText: 'Delete Route Points',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (!confirmed) return

  isDeletingAllRoutePoints.value = true
  try {
    if (props.tour) {
      const indices = deletableRoutePoints
        .filter((routePoint) => routePoint.tourId === props.tour?.clientId)
        .map((routePoint) => routePoint.index)
        .sort((left, right) => left - right)

      if (isLibraryContext.value) {
        await libraryStore.deleteRoutePoints(props.tour.clientId, indices)
      } else {
        await workspaceStore.deleteRoutePoints(props.tour.clientId, indices)
      }
    } else {
      const indicesByTour = new Map<string, number[]>()
      for (const routePoint of deletableRoutePoints) {
        const indices = indicesByTour.get(routePoint.tourId)
        if (indices) {
          indices.push(routePoint.index)
        } else {
          indicesByTour.set(routePoint.tourId, [routePoint.index])
        }
      }

      for (const [tourId, indices] of indicesByTour.entries()) {
        await workspaceStore.deleteRoutePoints(
          tourId,
          indices.sort((left, right) => left - right),
        )
      }
    }
  } finally {
    isDeletingAllRoutePoints.value = false
  }
}

const zoomToRoutePoint = (tourId: string, rpIndex: number) => {
  emit('zoom-route-point', tourId, rpIndex)
}

const toggleRoutePointVisibility = () => {
  uiStore.toggleRoutePointVisibility()
}

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
  emit('update:expanded', isExpanded.value)
}

// A and B are the route's anchors — only intermediate points carry a direct delete (X), matching the
// "A and B must stay in place" rule the bulk-delete already enforces.
const isDeletableRoutePoint = (item: RpItem): boolean => item.label !== 'A' && item.label !== 'B'

const effectiveRoutePointVisibility = computed(() => {
  if (!props.tour) return uiStore.areRoutePointsVisible
  const tourVisible = props.tour.visible !== undefined ? props.tour.visible : true
  return tourVisible && uiStore.areRoutePointsVisible
})

// --- Drag-to-reorder (pointer-based, works on touch + mouse) -------------------------------------
// Reordering happens within a single track group. While dragging we live-reorder a working copy of
// the group's client-id order so the list reflects the new position under the finger; on drop we emit
// the new order and keep it optimistically applied until the store's route points catch up (so the
// rows don't snap back during the BRouter re-route).
type DragState = {
  tourId: string
  trackClientId: string | null
  order: string[]
  activeClientId: string
}
const dragState = ref<DragState | null>(null)
const optimisticOrder = ref<DragState | null>(null)
const dragMoved = ref(false)
const suppressNextRowClick = ref(false)
const rowElsByClientId = ref<Map<string, HTMLElement>>(new Map())
let optimisticClearTimer: ReturnType<typeof setTimeout> | null = null

const setRowElByClientId = (el: Element | ComponentPublicInstance | null, clientId: string) => {
  if (el instanceof HTMLElement) rowElsByClientId.value.set(clientId, el)
  else rowElsByClientId.value.delete(clientId)
}

const matchesGroup = (state: DragState | null, group: RpGroup) =>
  !!state && state.tourId === group.tourId && state.trackClientId === group.trackClientId

// Items to render for a group, honoring an in-progress drag or a pending optimistic order.
const displayItems = (group: RpGroup): RpItem[] => {
  const order = matchesGroup(dragState.value, group)
    ? dragState.value!.order
    : matchesGroup(optimisticOrder.value, group)
      ? optimisticOrder.value!.order
      : null
  if (!order) return group.items
  const byClientId = new Map(group.items.map((it) => [it.routePoint.clientId, it]))
  const ordered = order.map((id) => byClientId.get(id)).filter((it): it is RpItem => !!it)
  // Fall back to the source order if the ids no longer line up (group changed underneath us).
  return ordered.length === group.items.length ? ordered : group.items
}

const isGroupReorderable = (group: RpGroup) =>
  !!props.reorderable && group.items.length > 2 && group.trackClientId !== undefined

const onHandlePointerDown = (group: RpGroup, item: RpItem, event: PointerEvent) => {
  if (event.button !== undefined && event.button > 0) return
  if (!isGroupReorderable(group)) return
  event.stopPropagation()
  event.preventDefault()
  dragState.value = {
    tourId: group.tourId,
    trackClientId: group.trackClientId,
    order: group.items.map((it) => it.routePoint.clientId),
    activeClientId: item.routePoint.clientId,
  }
  dragMoved.value = false
  window.addEventListener('pointermove', onDragMove, { passive: false })
  window.addEventListener('pointerup', onDragEnd)
  window.addEventListener('pointercancel', onDragEnd)
}

const onDragMove = (event: PointerEvent) => {
  const state = dragState.value
  if (!state) return
  event.preventDefault()
  let targetId: string | null = null
  for (const id of state.order) {
    const el = rowElsByClientId.value.get(id)
    if (!el) continue
    const rect = el.getBoundingClientRect()
    if (event.clientY >= rect.top && event.clientY <= rect.bottom) {
      targetId = id
      break
    }
  }
  if (!targetId || targetId === state.activeClientId) return
  const order = [...state.order]
  const from = order.indexOf(state.activeClientId)
  const to = order.indexOf(targetId)
  if (from === -1 || to === -1) return
  order.splice(from, 1)
  order.splice(to, 0, state.activeClientId)
  dragState.value = { ...state, order }
  dragMoved.value = true
}

const onDragEnd = () => {
  const state = dragState.value
  dragState.value = null
  window.removeEventListener('pointermove', onDragMove)
  window.removeEventListener('pointerup', onDragEnd)
  window.removeEventListener('pointercancel', onDragEnd)
  if (!state || !dragMoved.value) return
  // Suppress the click that follows the pointerup so we don't zoom the dragged row.
  suppressNextRowClick.value = true
  nextTick(() => {
    suppressNextRowClick.value = false
  })
  // Keep the new order applied until the store updates (or a safety timeout fires).
  optimisticOrder.value = state
  if (optimisticClearTimer) clearTimeout(optimisticClearTimer)
  optimisticClearTimer = setTimeout(() => {
    optimisticOrder.value = null
  }, 5000)
  emit('reorder-route-points', state.tourId, state.trackClientId, state.order)
}

// Once the store's route points match the optimistic order (re-route done), drop the override.
watch(routePointGroups, (groups) => {
  const pending = optimisticOrder.value
  if (!pending) return
  const group = groups.find(
    (g) => g.tourId === pending.tourId && g.trackClientId === pending.trackClientId,
  )
  if (!group) {
    optimisticOrder.value = null
    return
  }
  const actual = group.items.map((it) => it.routePoint.clientId)
  if (
    actual.length === pending.order.length &&
    actual.every((id, index) => id === pending.order[index])
  ) {
    optimisticOrder.value = null
    if (optimisticClearTimer) clearTimeout(optimisticClearTimer)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('pointermove', onDragMove)
  window.removeEventListener('pointerup', onDragEnd)
  window.removeEventListener('pointercancel', onDragEnd)
  if (optimisticClearTimer) clearTimeout(optimisticClearTimer)
})
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Header: in the planner (comfortable) the tab already shows the count, so the row is just a
         label + the bulk actions (hide all / delete intermediates); the dense sidebar (compact)
         keeps its collapsible "Route Points (n)" toggle. -->
    <div class="flex justify-between items-center px-4 pt-3 pb-1 group">
      <button
        v-if="!isComfortable"
        @click="toggleExpanded"
        class="flex items-center flex-1 hover:text-blue-600 focus:outline-none hover:cursor-pointer"
      >
        <ChevronDownIcon v-if="isExpanded" class="w-3 h-3 text-gray-500" />
        <ChevronRightIcon v-else class="w-3 h-3 text-gray-500" />
        <span class="text-xs font-semibold text-gray-700 ml-1">
          Route Points ({{ routePoints.length }})
        </span>
      </button>
      <span v-else class="flex-1 text-sm font-semibold text-gray-700">Route points</span>

      <div class="flex items-center gap-1">
        <button
          @click="toggleRoutePointVisibility"
          class="flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer rounded-lg transition-colors shrink-0 disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
          :class="isComfortable ? 'w-8 h-8' : 'w-5 h-5'"
          :disabled="isDeletingAllRoutePoints"
          :title="effectiveRoutePointVisibility ? 'Hide all route points' : 'Show all route points'"
          :aria-label="
            effectiveRoutePointVisibility ? 'Hide all route points' : 'Show all route points'
          "
        >
          <component
            :is="effectiveRoutePointVisibility ? EyeIcon : EyeOffIcon"
            :class="isComfortable ? 'w-4 h-4' : 'w-3.5 h-3.5'"
          />
        </button>
        <button
          v-if="props.canDeleteRoutePoints !== false"
          @click="deleteAllRoutePoints"
          :disabled="isBulkDeleteDisabled"
          class="flex items-center justify-center rounded-lg transition-colors shrink-0"
          :class="[
            isComfortable ? 'w-8 h-8' : 'w-5 h-5',
            isDeletingAllRoutePoints
              ? 'text-blue-500 cursor-progress'
              : canBulkDeleteRoutePoints
                ? 'text-gray-400 hover:text-red-600 hover:bg-red-50 hover:cursor-pointer'
                : 'text-gray-300 cursor-not-allowed',
          ]"
          :title="
            isDeletingAllRoutePoints
              ? 'Deleting route points'
              : canBulkDeleteRoutePoints
                ? 'Delete route points except A and B'
                : 'A and B must stay in place'
          "
        >
          <component
            :is="isDeletingAllRoutePoints ? Loader2Icon : Trash2Icon"
            :class="[
              isComfortable ? 'w-4 h-4' : 'w-3 h-3',
              { 'animate-spin': isDeletingAllRoutePoints },
            ]"
          />
        </button>
      </div>
    </div>

    <!-- Route point list - only rendered when expanded -->
    <div v-if="isExpanded" class="flex-1 px-4 pb-2">
      <div ref="routePointListRef">
        <template
          v-for="(group, gIdx) in routePointGroups"
          :key="`g-${group.tourId}-${group.trackClientId ?? 'none'}-${gIdx}`"
        >
          <div
            v-if="
              !props.hideTrackHeaders &&
              (routePointGroups.length > 1 ||
                (group.trackClientId !== null && (props.tour?.tracks?.length ?? 0) > 0))
            "
            class="flex items-center gap-1.5 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500"
          >
            <span
              v-if="group.trackColor"
              class="inline-block w-2 h-2 rounded-full"
              :style="{ backgroundColor: group.trackColor }"
            />
            <span class="truncate">{{ group.trackName }}</span>
          </div>
          <div
            v-for="item in displayItems(group)"
            :key="item.routePoint.clientId"
            :class="[
              'group/rp flex transition-colors duration-200 hover:cursor-pointer',
              isComfortable
                ? 'items-center gap-2 rounded-xl border px-2.5 py-2 min-h-12 mb-2'
                : 'items-start -mx-1 px-1 py-1.5 rounded',
              dragState && dragState.activeClientId === item.routePoint.clientId
                ? isComfortable
                  ? 'border-blue-300 bg-blue-50 ring-1 ring-blue-300 shadow-sm'
                  : 'bg-blue-50 ring-1 ring-blue-300 shadow-sm'
                : isComfortable
                  ? 'border-gray-200 bg-white hover:bg-gray-50 shadow-[0_1px_2px_rgba(15,23,42,0.04)]'
                  : 'hover:bg-gray-50',
            ]"
            :data-tour-id="item.tourId"
            :data-index="item.index"
            :ref="(el) => setRowElByClientId(el, item.routePoint.clientId)"
            @click="suppressNextRowClick ? undefined : zoomToRoutePoint(item.tourId, item.index)"
          >
            <!-- Start (A) green · end (B) red · intermediate blue — shared with the map markers. -->
            <div
              class="flex items-center justify-center rounded-full text-white font-bold shrink-0"
              :class="isComfortable ? 'w-7 h-7 text-xs' : 'w-4.5 h-4.5 text-[10px] mr-1.5 mt-px'"
              :style="{ backgroundColor: getRoutePointColor(item.label) }"
            >
              {{ item.label }}
            </div>

            <span
              class="flex-1 min-w-0 truncate text-gray-700"
              :title="item.routePoint.name || `Route Point ${item.label}`"
              :class="[
                isComfortable ? 'text-sm' : 'text-[11px]',
                { 'text-gray-400!': !effectiveRoutePointVisibility },
              ]"
            >
              {{ item.routePoint.name || `Route Point ${item.label}` }}
            </span>

            <div class="flex items-center shrink-0 gap-0.5" :class="isComfortable ? '' : 'ml-1'">
              <button
                v-if="isGroupReorderable(group)"
                type="button"
                class="shrink-0 rounded-md text-gray-400 transition-colors hover:text-gray-600 hover:cursor-grab active:cursor-grabbing touch-none"
                :class="isComfortable ? 'p-2' : 'p-1'"
                title="Drag to reorder"
                aria-label="Drag to reorder route point"
                @click.stop
                @pointerdown="onHandlePointerDown(group, item, $event)"
              >
                <GripVerticalIcon :class="isComfortable ? 'w-4 h-4' : 'w-3.5 h-3.5'" />
              </button>
              <button
                v-if="props.canDeleteRoutePoints !== false && isDeletableRoutePoint(item)"
                type="button"
                class="shrink-0 rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 hover:cursor-pointer"
                :class="isComfortable ? 'p-2' : 'p-1'"
                title="Delete route point"
                aria-label="Delete route point"
                @click.stop="deleteRoutePoint(item.tourId, item.index)"
              >
                <XIcon :class="isComfortable ? 'w-4 h-4' : 'w-3.5 h-3.5'" />
              </button>
            </div>
          </div>
        </template>

        <div
          v-if="routePoints.length === 0 && !isScreenSizeBelow(ScreenSize.MD)"
          class="py-3 text-center text-xs text-gray-400"
        >
          <p>No route points available.</p>
        </div>
      </div>
    </div>
  </div>
</template>
